import React, { Fragment } from "react";
import moment from "moment";
import { Grid } from "@material-ui/core";
import _ from "lodash";
import hedgeTypes from "constants/hedgeTypes";
import hedgeStatus from "constants/hedgeStatus";
import maturityTypes from "constants/maturityTypes";

import { ListItemLink, CreditChargeItem, GridItem, CurrencyItem, ItemLinks, NoItems, DeadItemLinks } from "../util";
import { ItemList } from "../styles";

const isWatch = selectedHedge => selectedHedge.hedgeStatusId === hedgeStatus.WATCH;
const renderSwapRate = selectedHedge => {
  if (isWatch(selectedHedge)) {
    return <GridItem value={selectedHedge.marketRate} label="Market Rate" mask={"percent"} />;
  }

  return <GridItem value={selectedHedge.rate} label="Rate" mask={"percent"} />;
};

const getColOneRowThree = selectedHedge => {
  switch (selectedHedge.hedgeTypeId) {
    case hedgeTypes.CAP:
      return <GridItem value={selectedHedge.strike} label="Strike" mask={"percent"} />;
    case hedgeTypes.COLLAR:
      return <GridItem value={selectedHedge.floor} label="Floor" mask={"percent"} />;
    case hedgeTypes.CORRIDOR:
      return <GridItem value={selectedHedge.lowerStrike} label="Lower Strike" mask={"percent"} />;
    case hedgeTypes.SWAP:
      return renderSwapRate(selectedHedge);
    default:
      return null;
  }
};

const getColOneRowFour = selectedHedge => {
  switch (selectedHedge.hedgeTypeId) {
    case hedgeTypes.COLLAR:
      return <GridItem value={selectedHedge.cap} label="Cap" mask={"percent"} />;
    case hedgeTypes.CORRIDOR:
      return <GridItem value={selectedHedge.upperStrike} label="Upper Strike" mask={"percent"} />;
    case hedgeTypes.SWAP:
      return <CreditChargeItem value={selectedHedge.creditCharge} label="Credit Charge (bps)" />;
    case hedgeTypes.CAP:
      return isWatch(selectedHedge) && <CurrencyItem value={selectedHedge.marketCost} label="Market Cost" />;
    default:
      return null;
  }
};

const getColOneRowFive = selectedHedge => {
  switch (selectedHedge.hedgeTypeId) {
    case hedgeTypes.CAP:
      return !isWatch(selectedHedge) && <GridItem value={selectedHedge.premium} label="Premium" mask={"currency"} />;
    case hedgeTypes.COLLAR:
    case hedgeTypes.CORRIDOR:
      return <GridItem value={selectedHedge.premium} label="Premium" mask={"currency"} />;
    default:
      return null;
  }
};

const getColTwoRowFour = selectedHedge => {
  const shouldShowEscrow = false;
  const isActive = selectedHedge.hedgeStatusId === hedgeStatus.ACTIVE;

  switch (selectedHedge.hedgeType.id) {
    case hedgeTypes.CAP:
      return (shouldShowEscrow && isActive) ? <GridItem value={selectedHedge.estReplacementCost} label="Escrow Cap Cost" mask={"currency"} /> : null;

    default:
      return null;
  }
};

const getActiveItemList = (hedges, selectedHedge, handleSelect) => (<ItemList id="activeHedgesList">
  {hedges
  /*
   * Add filter here once model has been decided
   * .filter(x => !x.taggedTo.hedgeTypeId)
   * Add active sort here once model has been decided
   */
    .sort((a, b) => moment.utc(a.effectiveDate).unix() - moment.utc(b.effectiveDate).unix())
    .map(item => <ListItemLink
      key={item.id}
      isSelected={selectedHedge.id === item.id}
      item={item}
      title={`${_.get(item, "hedgeProvider.name")} - ${_.get(item, "hedgeType.value")}`}
      handleClick={handleSelect}
    />)}
</ItemList>);

const getWatchItemList = (hedges, selectedHedge, handleSelect) => (<ItemList id="watchHedgesList">
  {hedges
  /*
   * Add filter here once model has been decided
   * .filter(x => !x.taggedTo.hedgeTypeId)
   * Add active sort here once model has been decided
   */
    .sort((a, b) => moment.utc(a.effectiveDate).unix() - moment.utc(b.effectiveDate).unix())
    .map(item => <ListItemLink
      key={item.id}
      isSelected={selectedHedge.id === item.id}
      item={item}
      title={`Watch - ${_.get(item, "hedgeType.value")}`}
      handleClick={handleSelect}
    />)}
</ItemList>);
const getTerminatedItemList = (hedges, selectedHedge, handleSelect) => (<ItemList id="terminatedHedgesList">
  {hedges
  // Add teminated sort here once model has been decided
    .sort((a, b) => moment.utc(a.earlyTerminationDate).unix() - moment.utc(b.earlyTerminationDate).unix())
    .map(item => <ListItemLink
      key={item.id}
      isSelected={selectedHedge.id === item.id}
      item={item}
      title={`${_.get(item, "hedgeProvider.name")} - ${_.get(item, "hedgeType.value")}`}
      handleClick={handleSelect}
    />)}
</ItemList>);

const getItemList = (hedgeStatusId, hedges, selectedHedge, handleSelect) => {
  switch (hedgeStatusId) {
    case hedgeStatus.TERMINATED:
      return getTerminatedItemList(hedges, selectedHedge, handleSelect);

    case hedgeStatus.ACTIVE:
      return getActiveItemList(hedges, selectedHedge, handleSelect);

    case hedgeStatus.WATCH:
      return getWatchItemList(hedges, selectedHedge, handleSelect);
  }
};

const hedgeCashflowUrl = (hedgeStatusId, fundId, dealId, selectedHedge) => {
  if (hedgeStatusId === hedgeStatus.TERMINATED) {
    return `/portfolio/funds/${fundId}/deals/${dealId}/cashflowsPage?hedgeId=${selectedHedge.id}`;
  }

  return `/portfolio/funds/${fundId}/deals/${dealId}/cashflowsPage?showActiveHedges=true&hedgeId=${selectedHedge.id}`;
};
const shouldShowCashflowsLink = hedgeStatusId => hedgeStatusId === hedgeStatus.TERMINATED || hedgeStatusId === hedgeStatus.ACTIVE;
export const ActiveHedgeComponent = ({ hedges, Link, fundId, dealId, handleSelect, selectedHedge, hedgeStatusId }) => {
  if (!hedges || hedges.length === 0) {
    const message = hedgeStatusId === hedgeStatus.TERMINATED ? "There are no terminated hedges for this deal." : "There are no hedges for this deal.";

    return <NoItems Message={message} />;
  }
  if (selectedHedge === null) {
    return null;
  }
  const isConstantMaturity = _.get(selectedHedge, "maturityTypeId") === maturityTypes.CONSTANT_MATURITY;
  const isWatch = selectedHedge.hedgeStatusId === hedgeStatus.WATCH;
  const isActive = selectedHedge.hedgeStatusId === hedgeStatus.ACTIVE;
  const isTerminated = selectedHedge.hedgeStatusId === hedgeStatus.TERMINATED;
  const isLenderRequired = _.get(selectedHedge, "isLenderRequired", false);
  const lenderRequiredText = isLenderRequired ? "Yes" : "No";

  return (
    <>
      <Grid container spacing={8} alignItems="flex-start" justify="flex-start" direction="row">
        <Grid item lg={3} md={4} sm={4} xs={4}>
          {getItemList(hedgeStatusId, hedges, selectedHedge, handleSelect)}
        </Grid>
        <Grid item lg={8} md={8} sm={8} xs={8}>
          <Grid container spacing={8} alignItems="flex-start" justify="flex-start" direction="row">
            <Grid item name={`leftHedgeInfoArea${selectedHedge.hedgeStatus.value}`} md={6} sm={12} xs={12}>
              <GridItem value={selectedHedge.hedgeType ? selectedHedge.hedgeType.value : "-"} label="Hedge Type" mask={"text"} />
              <GridItem value={selectedHedge.notional} label="Notional" mask={"currency"} />
              {getColOneRowThree(selectedHedge)}
              {getColOneRowFour(selectedHedge)}
              {getColOneRowFive(selectedHedge)}
              {isActive && <GridItem value={selectedHedge.markToMarket} label="Mark-To-Market" mask={"currency"} />}
              {isTerminated && <GridItem value={selectedHedge.terminationValue} label="Termination Value/(Cost)" mask={"currency"} /> }
            </Grid>
            <Grid item name={`rightHedgeInfoArea${selectedHedge.hedgeStatus.value}`} md={6} sm={12} xs={12}>
              <GridItem value={_.get(selectedHedge, "loan.lenderCompany.name", "Deal")} label="Tagged To" mask={"text"} />
              {(!isConstantMaturity || !isWatch) && <GridItem value={selectedHedge.term} label="Term (m)" mask={"text"} />}
              {(isConstantMaturity || !isWatch) && <GridItem value={selectedHedge.effectiveDate} label="Effective Date" mask={"date"} />}
              {(isConstantMaturity || !isWatch) && <GridItem value={selectedHedge.terminationDate} label="Termination Date" mask={"date"} />}
              {isActive && <GridItem value={lenderRequiredText} label="Required Hedge" mask={"text"} />}
              {getColTwoRowFour(selectedHedge)}
              {isTerminated && <GridItem value={selectedHedge.earlyTerminationDate} label="Early Termination Date" mask={"date"} />}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <ItemLinks
        itemName={`Hedge${selectedHedge.id}`}
        DetailsLink={`/hedge/${fundId}/${dealId}/${selectedHedge.id}`}

        CashflowsLink={hedgeCashflowUrl(hedgeStatusId, fundId, dealId, selectedHedge)}
        shouldShowCashflowsLink={shouldShowCashflowsLink(hedgeStatusId)}

        escrowLedgerLink={selectedHedge && selectedHedge.watchHedges && selectedHedge.watchHedges.length ?
          `/hedge/free/${dealId}/${selectedHedge.watchHedges[0].id}/escrowLedger/${selectedHedge.id}` :
        null}
        shouldShowEscrowLedgerLink={selectedHedge.watchHedges && selectedHedge.watchHedges.length > 0}

        Link={Link}
      />
    </>);
};
